import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Input, Select, Switch, Radio, Alert, Space, Tooltip,
} from 'antd';
import { captureAnalytics } from '../../actions/preload';
import DraggableModal from './DraggableModal';

const { Option } = Select;

// Maps an existing rule (or null) to the modal's local form state.
export const initialStateFromRule = (rule) => {
  if (!rule) {
    return {
      name: '',
      sourceType: 'org',
      sourceId: null,
      action: 'upload',
      categoryFilter: 'all',
      categoryIds: [],
      autoArchive: false,
      autoArchiveScope: 'inbox_only',
      active: true,
    };
  }
  let categoryFilter = 'all';
  if (rule.category_match_mode === 'all_except') {
    categoryFilter = (rule.category_ids || []).length > 0 ? 'except' : 'categorized';
  } else if ((rule.category_ids || []).length > 0) categoryFilter = 'only';
  return {
    name: rule.name || '',
    sourceType: ['FaxNumber', 'Email', 'Department'].includes(rule.source_type) ? rule.source_type : 'org',
    sourceId: rule.source_id || null,
    action: rule.action || 'upload',
    categoryFilter,
    categoryIds: rule.category_ids || [],
    autoArchive: !!rule.auto_archive,
    autoArchiveScope: rule.auto_archive_scope || 'inbox_only',
    active: rule.active !== false,
  };
};

// Create/edit modal for a self-serve AutoUploadRule, modeled on AddTaskRoutingRuleModal.
// v1 supports org-wide, per-fax-line, per-email-inbox, and per-department
// sources, a category filter, the action, and the auto-archive post-action.
// (The condition builder is a follow-up.)
export class UnconnectedAddAutoUploadRuleModal extends Component {
  constructor(props) {
    super(props);
    this.state = initialStateFromRule(props.editingRule);
    // Fax lines come from the cached login payload, not a network call — this
    // matches ToggleFeatures/ModmedQueuedFaxSettings and avoids a 404 + unmount warning.
    this.state.faxNumbers = JSON.parse(localStorage.getItem('associated_fax_numbers') || '[]');
  }

  isValid = () => {
    const {
      sourceType, sourceId, categoryFilter, categoryIds,
    } = this.state;
    if (sourceType !== 'org' && !sourceId) return false;
    if (['only', 'except'].includes(categoryFilter) && categoryIds.length === 0) return false;
    return true;
  };

  buildPayload = () => {
    const {
      name, sourceType, sourceId, action, categoryFilter, categoryIds,
      autoArchive, autoArchiveScope, active,
    } = this.state;
    const uploadsDocument = ['upload', 'upload_and_task'].includes(action);
    return {
      name: name || null,
      source_type: sourceType === 'org' ? null : sourceType,
      source_id: sourceType === 'org' ? null : sourceId,
      action,
      category_match_mode: ['categorized', 'except'].includes(categoryFilter) ? 'all_except' : 'any',
      category_ids: ['all', 'categorized'].includes(categoryFilter) ? [] : categoryIds,
      auto_archive: uploadsDocument && autoArchive,
      auto_archive_scope: uploadsDocument && autoArchive ? autoArchiveScope : null,
      active,
    };
  };

  handleOk = () => {
    const { onSubmit, editingRule } = this.props;
    onSubmit(this.buildPayload(), editingRule && editingRule.id);
  };

  handleSourceChange = (event) => {
    const sourceType = event.target.value;
    captureAnalytics('auto_upload_rule_source_type_selected', { source_type: sourceType });
    this.setState({ sourceType, sourceId: null });
  };

  handleDepartmentSelect = (sourceId) => {
    captureAnalytics('auto_upload_rule_department_selected', { source_type: 'Department' });
    this.setState({ sourceId });
  };

  render() {
    const {
      visible, onCancel, editingRule, categories, confirmLoading, canCreateTasks, emailSources, departments,
    } = this.props;
    const {
      name, sourceType, sourceId, action, categoryFilter, categoryIds,
      autoArchive, autoArchiveScope, active, faxNumbers,
    } = this.state;
    const uploadsDocument = ['upload', 'upload_and_task'].includes(action);

    return (
      <DraggableModal
        visible={visible}
        title={editingRule ? 'Edit auto-upload rule' : 'Add auto-upload rule'}
        onCancel={onCancel}
        onOk={this.handleOk}
        okText={editingRule ? 'Save' : 'Create'}
        okButtonProps={{ disabled: !this.isValid(), loading: confirmLoading }}
        width={560}
        destroyOnClose
      >
        <Form layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Rules are evaluated by priority (top first). The first matching rule decides what happens to a document."
            description="A department rule covers every fax line and email inbox that department owns. If a line-specific rule also matches, the higher rule wins."
          />

          <Form.Item label="Name (optional)">
            <Input
              placeholder="e.g. Referrals on the main line"
              value={name}
              onChange={(e) => this.setState({ name: e.target.value })}
            />
          </Form.Item>

          <Form.Item label="Applies to">
            <Radio.Group
              value={sourceType}
              onChange={this.handleSourceChange}
            >
              <Space direction="vertical">
                <Radio value="org">All documents (org-wide default)</Radio>
                <Radio value="FaxNumber">A specific fax line</Radio>
                <Radio value="Email">A specific email inbox</Radio>
                <Radio value="Department">A specific department</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {sourceType === 'FaxNumber' && (
            <Form.Item label="Fax line" required>
              <Select
                showSearch
                placeholder="Select a fax line"
                optionFilterProp="children"
                value={sourceId}
                onChange={(value) => this.setState({ sourceId: value })}
              >
                {faxNumbers.map((faxEntry) => {
                  const [number, ownerName, , , faxId] = faxEntry || [];
                  if (!faxId) return null;
                  return (
                    <Option key={faxId} value={faxId}>
                      {number}
                      {ownerName ? ` — ${ownerName}` : ''}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}

          {sourceType === 'Email' && (
            <Form.Item label="Email inbox" required>
              <Select
                showSearch
                placeholder="Select an email inbox"
                optionFilterProp="children"
                value={sourceId}
                onChange={(value) => this.setState({ sourceId: value })}
              >
                {emailSources.map((email) => (
                  <Option key={email.id} value={email.id}>
                    {email.email_address}
                    {email.owner_name ? ` — ${email.owner_name}` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {sourceType === 'Department' && (
            <Form.Item label="Department" required>
              <Select
                showSearch
                placeholder="Select a department"
                optionFilterProp="children"
                value={sourceId}
                onChange={this.handleDepartmentSelect}
              >
                {departments.map((department) => (
                  <Option key={department.id} value={department.id}>{department.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="When a document matches, then"
            extra={action === 'upload_and_task'
              ? 'The task type is picked per document by your Task Routing Rules (or the fax line\'s task template). If no task type applies to a document, this rule is skipped and the next rule is tried.'
              : null}
          >
            <Select value={action} onChange={(value) => this.setState({ action: value })}>
              <Option value="upload">Upload to the EMR</Option>
              <Option value="upload_and_task" disabled={!canCreateTasks}>
                {canCreateTasks ? (
                  'Upload to the EMR and create a task'
                ) : (
                  <Tooltip title="Requires the task auto-upload feature. Contact support to enable it.">
                    Upload to the EMR and create a task
                  </Tooltip>
                )}
              </Option>
              <Option value="skip">Do not auto-upload (skip)</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Document categories">
            <Select value={categoryFilter} onChange={(value) => this.setState({ categoryFilter: value })}>
              <Option value="all">All categories</Option>
              <Option value="categorized">All categorized documents</Option>
              <Option value="only">Only the selected categories</Option>
              <Option value="except">All categories except the selected</Option>
            </Select>
          </Form.Item>

          {['only', 'except'].includes(categoryFilter) && (
            <Form.Item label="Categories" required>
              <Select
                mode="multiple"
                showSearch
                placeholder="Select categories"
                optionFilterProp="children"
                value={categoryIds}
                onChange={(value) => this.setState({ categoryIds: value })}
              >
                {(categories || []).map((cat) => (
                  <Option key={cat.id} value={cat.id}>{cat.emr_category}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {uploadsDocument && (
            <Form.Item
              label="Archive after successful upload"
              extra="Archive matching received faxes after the EMR confirms a successful upload."
            >
              <Switch
                checked={autoArchive}
                onChange={(checked) => this.setState({ autoArchive: checked })}
              />
            </Form.Item>
          )}

          {uploadsDocument && autoArchive && (
            <Form.Item label="Archive records from">
              <Select
                value={autoArchiveScope}
                onChange={(value) => this.setState({ autoArchiveScope: value })}
              >
                <Option value="inbox_only">Inbox only</Option>
                <Option value="rm_only">Referral Management only</Option>
                <Option value="both">Both</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item label="Active">
            <Switch checked={active} onChange={(checked) => this.setState({ active: checked })} />
          </Form.Item>
        </Form>
      </DraggableModal>
    );
  }
}

UnconnectedAddAutoUploadRuleModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editingRule: PropTypes.object,
  categories: PropTypes.array,
  confirmLoading: PropTypes.bool,
  canCreateTasks: PropTypes.bool,
  emailSources: PropTypes.array,
  departments: PropTypes.array,
};

UnconnectedAddAutoUploadRuleModal.defaultProps = {
  editingRule: null,
  categories: [],
  confirmLoading: false,
  canCreateTasks: false,
  emailSources: [],
  departments: [],
};

export default UnconnectedAddAutoUploadRuleModal;
